import React, { useState, useRef, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || '/api';
const GRADIENT = 'linear-gradient(135deg, #7F77DD, #1D9E75)';

const inputStyle = {
  padding: '10px 14px',
  borderRadius: 8,
  background: 'rgba(255,255,255,0.06)',
  border: '0.5px solid rgba(255,255,255,0.12)',
  color: '#f0efe9',
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const sendBtnStyle = {
  padding: '10px 20px',
  borderRadius: 8,
  background: GRADIENT,
  border: 'none',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0,
  transition: 'opacity 0.15s',
};

function FloAvatar() {
  return (
    <div style={{ width: 26, height: 26, borderRadius: 7, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'flex-end' }}>
      <span style={{ color: '#fff', fontWeight: 800, fontSize: 12 }}>F</span>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
      <FloAvatar />
      <div style={{ padding: '10px 14px', borderRadius: '14px 14px 14px 4px', background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.1)', display: 'flex', gap: 4, alignItems: 'center' }}>
        {[0, 1, 2].map(d => (
          <span key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(240,239,233,0.45)', display: 'inline-block', animation: `flo-bounce 1.2s ease-in-out ${d * 0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [phase, setPhase] = useState('identity');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]); // { role: 'user'|'assistant', content } — API format
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  function startChat(e) {
    e.preventDefault();
    const n = draftName.trim();
    const em = draftEmail.trim();
    setName(n);
    setEmail(em);
    setMessages([{ role: 'agent', text: `Hi ${n}! I'm FLO. Ask me anything about the platform, pricing, or how it works.` }]);
    setPhase('chat');
  }

  async function send(e) {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message: text, history }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      const reply = data.reply || data.message || JSON.stringify(data);
      setMessages(prev => [...prev, { role: 'agent', text: reply }]);
      setHistory(prev => [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: reply },
      ]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'agent', text: `Sorry, something went wrong: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(e);
    }
  }

  const shell = {
    background: 'rgba(255,255,255,0.03)',
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 560,
    margin: '0 auto',
  };

  if (phase === 'identity') {
    return (
      <>
        <style>{`@keyframes flo-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}`}</style>
        <div style={shell}>
          <div style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 18px rgba(127,119,221,0.35)' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>F</span>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f0efe9', lineHeight: 1.2 }}>FLO</div>
                <div style={{ fontSize: 11, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  Online now
                </div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'rgba(240,239,233,0.55)', lineHeight: 1.7, margin: 0 }}>
              Quick intro before we chat — just your name and email so we can follow up if needed.
            </p>
            <form onSubmit={startChat} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input type="text" placeholder="Your name" value={draftName} onChange={e => setDraftName(e.target.value)} required style={inputStyle} />
                <input type="email" placeholder="Your email" value={draftEmail} onChange={e => setDraftEmail(e.target.value)} required style={inputStyle} />
              </div>
              <button type="submit" style={{ ...sendBtnStyle, padding: '11px 22px', alignSelf: 'flex-start' }}>
                Start chatting →
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`@keyframes flo-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}`}</style>
      <div style={{ ...shell, height: 500 }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.02)', flexShrink: 0 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>F</span>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#f0efe9', lineHeight: 1.2 }}>FLO</div>
            <div style={{ fontSize: 11, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
              Online
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 8 }}>
              {msg.role === 'agent' && <FloAvatar />}
              <div style={{
                maxWidth: '74%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: msg.role === 'user' ? GRADIENT : 'rgba(255,255,255,0.06)',
                border: msg.role === 'agent' ? '0.5px solid rgba(255,255,255,0.1)' : 'none',
                fontSize: 14,
                color: '#f0efe9',
                lineHeight: 1.65,
                wordBreak: 'break-word',
              }}>
                {msg.text}
              </div>
            </div>
          ))}
          {loading && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <form onSubmit={send} style={{ padding: '10px 12px', borderTop: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8, background: 'rgba(255,255,255,0.02)', flexShrink: 0, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Type a message…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            style={{ ...sendBtnStyle, opacity: loading || !input.trim() ? 0.45 : 1, cursor: loading || !input.trim() ? 'default' : 'pointer' }}
          >
            Send
          </button>
        </form>
      </div>
    </>
  );
}
